import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setFormStatus(null);

    const form = e.currentTarget;
    const honeypot = (form.querySelector('input[name="website_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus({ type: 'success', message: '感谢您的留言，我们会尽快回复！' });
      setSubmitting(false);
      form.reset();
      return;
    }

    try {
      const formData = new FormData(form);
      const response = await fetch(
        'https://readdy.ai/api/form/d9spu258o1vn02ekuk00',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
        }
      );
      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus({ type: 'success', message: '感谢您的留言，我们会尽快回复！' });
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后重试';
        setFormStatus({ type: 'error', message: serverMsg });
      }
    } catch {
      setFormStatus({ type: 'error', message: '网络错误，请检查网络连接后重试' });
    } finally {
      setSubmitting(false);
    }
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '欧美MV大全',
    url: typeof window !== 'undefined' ? window.location.origin : '',
    description: '专注欧美音乐视频分享平台，提供最新最热欧美MV在线观看服务',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      availableLanguage: ['Chinese'],
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '欧美MV大全提供哪些类型的音乐视频？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '欧美MV大全涵盖流行、摇滚、嘻哈、电子、节奏蓝调、拉丁等6大音乐风格，汇聚全球最新最热欧美MV，第一时间追踪Billboard榜单热门。',
        },
      },
      {
        '@type': 'Question',
        name: '网站上的MV是否可以免费观看？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '是的，欧美MV大全上的所有MV均可在线免费观看，无需注册或付费，让您随时随地畅享高清欧美音乐视频。',
        },
      },
      {
        '@type': 'Question',
        name: '如何找到我喜欢的欧美歌手MV？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '您可以通过首页的分类导航，或者顶部的导航菜单按音乐风格浏览MV。我们也会在首页推荐热门和最新的欧美MV，帮助您发现喜爱的歌手新作。',
        },
      },
      {
        '@type': 'Question',
        name: '网站上的MV更新频率如何？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '欧美MV大全密切关注Billboard等权威榜单，第一时间更新最新的欧美MV，确保您不会错过任何热门音乐视频。',
        },
      },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(orgSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: '关于我们' },
            ]}
          />

          <section className="w-full px-4 md:px-6 py-10 md:py-16 bg-foreground-950 text-white">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="font-heading text-3xl md:text-5xl font-bold mb-4">关于欧美MV大全</h1>
              <p className="text-base md:text-lg text-foreground-300 max-w-2xl mx-auto">
                您的首选欧美音乐视频在线观看平台
              </p>
            </div>
          </section>

          <section className="w-full px-4 md:px-6 py-14 md:py-20">
            <div className="max-w-5xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                <div>
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                    品牌故事
                  </h2>
                  <div className="space-y-4 text-foreground-700 leading-relaxed">
                    <p>
                      欧美MV大全创立于2024年，致力于为全球华语用户提供一个便捷、高品质的欧美音乐视频观看平台。我们深知欧美音乐在华语地区的巨大影响力，因此聚集了一批热爱欧美音乐的专业团队，精心挑选和整理最值得观看的欧美MV。
                    </p>
                    <p>
                      从Billboard Hot 100到MTV音乐录影带大奖获奖作品，从经典摇滚到现代嘻哈，从独立R&B到热门拉丁，我们覆盖了广泛的欧美音乐风格。无论你是资深欧美乐迷还是刚刚接触欧美音乐的新听众，都能在这里找到属于自己的音乐角落。
                    </p>
                    <p>
                      我们的使命是让每一位用户都能第一时间欣赏到最新、最热的欧美音乐视频，感受欧美音乐的魅力与多样性。我们持续追踪全球音乐榜单，确保内容始终新鲜、及时。
                    </p>
                  </div>
                </div>
                <div className="relative aspect-[4/3] rounded-xl overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=A%20warm%20and%20artistic%20music%20studio%20scene%20with%20vinyl%20records%20on%20wall%2C%20headphones%20on%20wooden%20desk%2C%20soft%20ambient%20lighting%2C%20cozy%20creative%20workspace%2C%20music%20production%20equipment%2C%20warm%20amber%20tones%2C%20atmospheric%20interior&width=800&height=600&seq=about-brand-01&orientation=landscape"
                    alt="欧美MV大全品牌故事"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
            </div>
          </section>

          <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-secondary-50/50">
            <div className="max-w-5xl mx-auto">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 text-center mb-12">
                我们的数据
              </h2>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="text-center p-6 rounded-xl bg-background-50">
                  <p className="font-heading text-3xl md:text-4xl font-bold text-primary-500">12+</p>
                  <p className="text-sm text-foreground-500 mt-2">精选MV</p>
                </div>
                <div className="text-center p-6 rounded-xl bg-background-50">
                  <p className="font-heading text-3xl md:text-4xl font-bold text-accent-500">6</p>
                  <p className="text-sm text-foreground-500 mt-2">音乐风格</p>
                </div>
                <div className="text-center p-6 rounded-xl bg-background-50">
                  <p className="font-heading text-3xl md:text-4xl font-bold text-primary-500">10亿+</p>
                  <p className="text-sm text-foreground-500 mt-2">累计播放</p>
                </div>
                <div className="text-center p-6 rounded-xl bg-background-50">
                  <p className="font-heading text-3xl md:text-4xl font-bold text-accent-500">24/7</p>
                  <p className="text-sm text-foreground-500 mt-2">全天候更新</p>
                </div>
              </div>
            </div>
          </section>

          <section className="w-full px-4 md:px-6 py-14 md:py-20">
            <div className="max-w-5xl mx-auto">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 text-center mb-4">
                用户口碑
              </h2>
              <p className="text-sm text-foreground-500 text-center mb-10">来自欧美乐迷的真实评价</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="p-6 rounded-xl bg-background-50 border border-background-200/70">
                  <div className="flex items-center gap-1 mb-3 text-accent-500">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                    "欧美MV大全是我每天必逛的网站，更新速度快，分类也很清晰。无论是Taylor Swift还是Bad Bunny，都能第一时间看到最新MV，太赞了！"
                  </p>
                  <p className="text-xs text-foreground-500 font-medium">— 资深欧美乐迷 小林</p>
                </div>
                <div className="p-6 rounded-xl bg-background-50 border border-background-200/70">
                  <div className="flex items-center gap-1 mb-3 text-accent-500">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                    "作为一个嘻哈爱好者，这里的嘻哈MV真的太全了！Lil Nas X、Doja Cat的最新作品这里都有，画质也很清晰，强烈推荐！"
                  </p>
                  <p className="text-xs text-foreground-500 font-medium">— 嘻哈文化博主 Mike</p>
                </div>
                <div className="p-6 rounded-xl bg-background-50 border border-background-200/70">
                  <div className="flex items-center gap-1 mb-3 text-accent-500">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                    "界面简洁大方，没有广告干扰，体验非常好。喜欢这里的分类方式，按风格找MV太方便了，一直在用！"
                  </p>
                  <p className="text-xs text-foreground-500 font-medium">— 独立音乐人 Alex</p>
                </div>
              </div>
            </div>
          </section>

          <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-foreground-950 text-white">
            <div className="max-w-5xl mx-auto">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-white text-center mb-4">
                常见问题
              </h2>
              <p className="text-sm text-foreground-400 text-center mb-10">关于欧美MV大全的常见疑问</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl mx-auto">
                {faqSchema.mainEntity.map((item, index) => (
                  <div key={index} className="p-5 rounded-xl bg-foreground-900/50">
                    <h4 className="font-heading text-base font-semibold text-white mb-2">
                      {item.name}
                    </h4>
                    <p className="text-sm text-foreground-400 leading-relaxed">
                      {item.acceptedAnswer.text}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section id="contact" className="w-full px-4 md:px-6 py-14 md:py-20">
            <div className="max-w-2xl mx-auto">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 text-center mb-4">
                联系我们
              </h2>
              <p className="text-sm text-foreground-500 text-center mb-10">
                有任何建议或问题？欢迎给我们留言
              </p>

              <form
                id="contact-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="space-y-5"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-4 py-3 rounded-lg border border-background-200 bg-background-50 text-foreground-950 text-sm focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-4 py-3 rounded-lg border border-background-200 bg-background-50 text-foreground-950 text-sm focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">主题</label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    required
                    className="w-full px-4 py-3 rounded-lg border border-background-200 bg-background-50 text-foreground-950 text-sm focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                    placeholder="请输入留言主题"
                  />
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">留言内容</label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={5}
                    maxLength={500}
                    className="w-full px-4 py-3 rounded-lg border border-background-200 bg-background-50 text-foreground-950 text-sm focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
                    placeholder="请输入您的留言内容（不超过500字）"
                  ></textarea>
                  <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
                </div>

                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="honeypot-field"
                />

                <div className="text-center">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-8 py-3 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 text-white font-medium rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer"
                  >
                    {submitting ? (
                      <span className="flex items-center gap-2">
                        <i className="ri-loader-4-line animate-spin"></i>
                        发送中...
                      </span>
                    ) : '发送留言'}
                  </button>
                </div>

                {formStatus && (
                  <div
                    className={`p-4 rounded-lg text-sm text-center ${
                      formStatus.type === 'success'
                        ? 'bg-accent-100 text-accent-800'
                        : 'bg-primary-100 text-primary-800'
                    }`}
                  >
                    {formStatus.message}
                  </div>
                )}
              </form>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}